/**
 * Importador masivo de clientes desde CSV.
 *
 * Tres pasos y en este orden a propósito: eliges el fichero, VES lo que se ha
 * entendido —incluidos los duplicados y lo que está mal— y solo entonces
 * declaras de dónde sale la lista y creas. El botón de crear no aparece hasta
 * que hay algo que crear y está escrito el origen.
 */
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, FileUp, Loader2, Upload } from 'lucide-react'
import Modal from '../ui/Modal'
import { api, apiErrorMessage } from '../../lib/api'
import { toast } from '../../lib/dialogs'
import type {
  ClientImportPreviewResponse,
  ClientImportRow,
  CommitClientImportResponse,
} from '@jr2os/shared'

const ESTADO: Record<ClientImportRow['status'], string> = {
  nuevo: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  duplicado: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  error: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40',
}

interface Props {
  onClose: () => void
  onImported: () => void
}

export default function ImportClientsModal({ onClose, onImported }: Props) {
  const { t } = useTranslation('clients')
  const [preview, setPreview] = useState<ClientImportPreviewResponse | null>(null)
  const [origin, setOrigin] = useState('')
  const [busy, setBusy] = useState(false)

  const elegir = useCallback(
    async (file: File) => {
      setBusy(true)
      try {
        const form = new FormData()
        form.append('file', file)
        setPreview(await api.upload<ClientImportPreviewResponse>('/clients/import/preview', form))
      } catch (err) {
        toast.error(apiErrorMessage(err, t('import.previewError')))
      } finally {
        setBusy(false)
      }
    },
    [t],
  )

  const importar = async () => {
    if (!preview) return
    setBusy(true)
    try {
      const r = await api.post<CommitClientImportResponse>('/clients/import/commit', {
        fileName: preview.fileName,
        origin,
        filas: preview.filas,
      })
      toast.success(t('import.done', { creados: r.creados }))
      onImported()
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err, t('import.commitError')))
    } finally {
      setBusy(false)
    }
  }

  const resumen = preview?.resumen
  const puedeImportar = !!resumen && resumen.nuevos > 0 && origin.trim().length >= 10

  return (
    <Modal open onClose={onClose} title={t('import.title')} description={t('import.subtitle')} maxWidth="max-w-3xl">
      <div className="space-y-4">
        {!preview && (
          <label className="flex flex-col items-center justify-center gap-2 p-8 rounded-lg border border-dashed border-base cursor-pointer hover:bg-hover">
            {busy ? <Loader2 size={22} className="animate-spin t-muted" /> : <FileUp size={22} className="t-muted" />}
            <span className="text-sm t-secondary">{t('import.pick')}</span>
            <span className="text-xs t-faint text-center max-w-sm">{t('import.pickHint')}</span>
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void elegir(f)
              }}
            />
          </label>
        )}

        {preview && resumen && (
          <>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`px-2 py-1 rounded border ${ESTADO.nuevo}`}>
                {t('import.countNew', { n: resumen.nuevos })}
              </span>
              {resumen.duplicados > 0 && (
                <span className={`px-2 py-1 rounded border ${ESTADO.duplicado}`}>
                  {t('import.countDup', { n: resumen.duplicados })}
                </span>
              )}
              {resumen.errores > 0 && (
                <span className={`px-2 py-1 rounded border ${ESTADO.error}`}>
                  {t('import.countErr', { n: resumen.errores })}
                </span>
              )}
              {resumen.etiquetasNuevas.length > 0 && (
                <span className="px-2 py-1 rounded border bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-200 border-fuchsia-500/40">
                  {t('import.newTags', { list: resumen.etiquetasNuevas.join(', ') })}
                </span>
              )}
            </div>

            {preview.columnasIgnoradas.length > 0 && (
              <div className="flex items-start gap-2 text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-300">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{t('import.ignoredColumns', { list: preview.columnasIgnoradas.join(', ') })}</span>
              </div>
            )}

            <div className="max-h-72 overflow-auto rounded-lg border border-base">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[var(--bg-surface)] z-10">
                  <tr className="text-left t-muted">
                    <th className="px-2 py-1.5 font-medium">#</th>
                    <th className="px-2 py-1.5 font-medium">{t('import.colName')}</th>
                    <th className="px-2 py-1.5 font-medium">{t('import.colEmail')}</th>
                    <th className="px-2 py-1.5 font-medium">{t('import.colTags')}</th>
                    <th className="px-2 py-1.5 font-medium">{t('import.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.filas.map((f) => (
                    <tr key={f.numero} className="border-t border-base/60">
                      <td className="px-2 py-1.5 t-faint font-mono">{f.numero}</td>
                      <td className="px-2 py-1.5 t-primary">
                        {f.displayName || <span className="t-faint">—</span>}
                        {f.typeInferido && (
                          <span className="ml-1 t-faint" title={t('import.typeGuessed')}>
                            ({t(`type.${f.type === 'EMPRESA' ? 'empresa' : 'personaFisica'}`)}?)
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 t-secondary">{f.email ?? '—'}</td>
                      <td className="px-2 py-1.5 t-secondary">{f.tags.join(', ') || '—'}</td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded border ${ESTADO[f.status]}`}>
                          {t(`import.status.${f.status}`)}
                        </span>
                        {f.motivo && <div className="t-faint mt-0.5">{f.motivo}</div>}
                        {f.duplicadoDe && <div className="t-faint">→ {f.duplicadoDe}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="import-origin" className="text-sm font-medium t-primary block">
                {t('import.originLabel')}
              </label>
              <p className="text-xs t-muted">{t('import.originHelp')}</p>
              <textarea
                id="import-origin"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                rows={2}
                placeholder={t('import.originPlaceholder')}
                className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:border-jr2-500/50"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 rounded-lg text-sm t-secondary hover:bg-hover"
              >
                {t('common:actions.cancel')}
              </button>
              <button
                type="button"
                disabled={!puedeImportar || busy}
                onClick={() => void importar()}
                className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {t('import.submit', { n: resumen.nuevos })}
              </button>
            </div>
            {resumen.nuevos > 0 && origin.trim().length < 10 && (
              <p className="text-xs t-faint flex items-center gap-1 justify-end">
                <CheckCircle2 size={12} /> {t('import.originRequired')}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
